// Read-only aggregator over every chain-specific wallet hook. Used by
// the header to render chips + "Disconnect all", and by the recovery
// page when an action's chain-side dispatch needs to confirm the
// matching wallet is live before signing.

import { useCallback, useMemo, useRef } from "react";
import { useAccount as useWagmiAccount, useDisconnect as useWagmiDisconnect } from "wagmi";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { useAccount as useStarknetAccount, useDisconnect as useStarknetDisconnect } from "@starknet-react/core";
import { useNearWallet } from "@/wallet/near";
import { useBtcWallet } from "@/wallet/btc";

export type WalletKind = "near" | "evm" | "sol" | "btc" | "starknet";

export interface ConnectedWallet {
  kind: WalletKind;
  account: string;
}

export function useConnectedWallets() {
  const near = useNearWallet();
  const evm = useWagmiAccount();
  const sol = useSolanaWallet();
  const stark = useStarknetAccount();
  const btc = useBtcWallet();

  const { disconnectAsync: disconnectEvm } = useWagmiDisconnect();
  const { disconnect: disconnectStarknet } = useStarknetDisconnect();

  const connected: ConnectedWallet[] = useMemo(() => {
    const out: ConnectedWallet[] = [];
    if (near.account) out.push({ kind: "near", account: near.account });
    if (evm.isConnected && evm.address) out.push({ kind: "evm", account: evm.address });
    if (sol.publicKey) out.push({ kind: "sol", account: sol.publicKey.toBase58() });
    if (btc.account) out.push({ kind: "btc", account: btc.account });
    if (stark.address) out.push({ kind: "starknet", account: stark.address });
    return out;
  }, [near.account, evm.isConnected, evm.address, sol.publicKey, btc.account, stark.address]);

  // Guard against re-entry from double-clicks on the header chip:
  // calling each wallet's disconnect twice concurrently throws on
  // some adapters (wagmi's `disconnectAsync`, Solana's) and the
  // resulting Promise.allSettled rejections only surface as console
  // noise. Drop concurrent invocations.
  const inFlight = useRef(false);
  const disconnectAll = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const tasks: Array<Promise<unknown>> = [];
      if (near.account) tasks.push(near.disconnect());
      if (evm.isConnected) tasks.push(disconnectEvm());
      if (sol.connected) tasks.push(sol.disconnect());
      if (btc.account) tasks.push(btc.disconnect());
      // starknet-react v5's `useDisconnect().disconnect` returns void;
      // wrap so a thrown error is captured by Promise.allSettled rather
      // than escaping to the unhandled-rejection handler.
      if (stark.address) tasks.push((async () => disconnectStarknet())());
      await Promise.allSettled(tasks);
    } finally {
      inFlight.current = false;
    }
  }, [near, evm.isConnected, disconnectEvm, sol, btc, stark.address, disconnectStarknet]);

  return { connected, disconnectAll };
}
